// reportService.js — ✅ OPTIMISÉ : 1 appel API au lieu de 5x /employee/sales
//
// AVANT : 7 appels parallèles dont 5 chargent /employee/sales (toutes les ventes)
//         puis recalculent tout en JavaScript → ~5000 objets transférés
//
// APRÈS : 1 seul appel GET /manager/reports/full?period=...
//         Le backend retourne 8 rubriques pré-calculées en SQL
//
// FALLBACK : Si /reports/full n'est pas encore déployé, bascule automatiquement
//            sur les anciens endpoints /manager/stats/* et /manager/team/*

import { apiCall } from './api';

// ─────────────────────────────────────────────
// UTILITAIRES
// ─────────────────────────────────────────────

const ensureArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'object') {
    for (const key of ['data', 'results', 'items', 'content']) {
      if (data[key] && Array.isArray(data[key])) return data[key];
    }
  }
  return [];
};

// ─────────────────────────────────────────────
// SERVICE PRINCIPAL
// ─────────────────────────────────────────────

const reportService = {

  /**
   * ✅ RAPPORT COMPLET EN 1 APPEL — 8 rubriques
   *
   * @param {string} period - 'today' | 'week' | 'month' | 'year' | 'custom'
   * @param {string|null} startDate - YYYY-MM-DD (si period='custom')
   * @param {string|null} endDate   - YYYY-MM-DD (si period='custom')
   *
   * @returns {Object} { success, data: { summary, revenueTimeline, products, customers, payments, team, stock, categories } }
   */
  getFullReport: async (period = 'today', startDate = null, endDate = null) => {
    try {
      console.log('📊 Chargement rapport — période:', period);

      const params = new URLSearchParams({ period });
      if (period === 'custom' && startDate) params.append('startDate', startDate);
      if (period === 'custom' && endDate) params.append('endDate', endDate);

      const response = await apiCall.get(`/manager/reports/full?${params.toString()}`);

      if (response && response.success && response.data) {
        const d = response.data;
        console.log('✅ Rapport reçu — 8 rubriques');

        return {
          success: true,
          data: {
            period: d.period || period,
            periodStart: d.periodStart || null,
            periodEnd: d.periodEnd || null,

            // ── 1. Résumé Financier ──
            summary: {
              revenue:          d.summary?.revenue ?? 0,
              revenueGrowth:    d.summary?.revenueGrowth ?? 0,
              salesCount:       d.summary?.salesCount ?? 0,
              salesGrowth:      d.summary?.salesGrowth ?? 0,
              averageBasket:    d.summary?.averageBasket ?? 0,
              expenses:         d.summary?.expenses ?? 0,
              netProfit:        d.summary?.netProfit ?? 0,
              profitMargin:     d.summary?.profitMargin ?? 0,
              creditBalance:    d.summary?.creditBalance ?? 0,
              previousRevenue:  d.summary?.previousRevenue ?? 0,
              previousSalesCount: d.summary?.previousSalesCount ?? 0,
            },

            // ── 2. Évolution Revenus ──
            revenueTimeline: ensureArray(d.revenueTimeline).map(item => ({
              date:       item.date || '',
              salesCount: item.salesCount || item.sales_count || 0,
              revenue:    item.revenue || 0,
            })),

            // ── 3. Produits ──
            products: {
              topSelling: ensureArray(d.products?.topSelling).map(p => ({
                productId:    p.product_id   || p.productId || p.id,
                productName:  p.product_name || p.productName || p.name || 'Produit',
                categoryName: p.category_name || p.categoryName || 'N/A',
                totalSold:    p.total_sold   || p.totalSold || 0,
                totalRevenue: p.total_revenue || p.totalRevenue || 0,
                saleCount:    p.sale_count   || p.saleCount || 0,
                currentStock: p.current_stock || p.currentStock || 0,
                todaySold:    p.today_sold   || p.todaySold || 0,
              })),
              abcSummary: d.products?.abcSummary || {},
              abcDetails: ensureArray(d.products?.abcDetails).map(item => ({
                productName:      item.productName || '',
                revenue:          item.revenue || 0,
                cumulativePercent: item.cumulativePercent || 0,
                abcCategory:      item.abcCategory || 'C',
              })),
              neverSold: ensureArray(d.products?.neverSold).map(item => ({
                productId:   item.productId || '',
                productName: item.productName || '',
                price:       item.price || 0,
                createdAt:   item.createdAt || '',
              })),
              neverSoldCount: d.products?.neverSoldCount ?? 0,
            },

            // ── 4. Clients ──
            customers: {
              stats: {
                total:       d.customers?.stats?.total ?? 0,
                vipCount:    d.customers?.stats?.vipCount ?? 0,
                resellerCount: d.customers?.stats?.resellerCount ?? 0,
                withPurchases: d.customers?.stats?.withPurchases ?? 0,
                totalPurchasesAmount: d.customers?.stats?.totalPurchasesAmount ?? 0,
              },
              topByRevenue: ensureArray(d.customers?.topByRevenue).map(c => ({
                customerName: c.customerName || c.customer_name || 'Client',
                orderCount:   c.orderCount   || c.order_count || 0,
                totalSpent:   c.totalSpent   || c.total_spent || 0,
                lastPurchase: c.lastPurchase  || c.last_purchase || null,
              })),
              creditDebtors: ensureArray(d.customers?.creditDebtors).map(c => ({
                customerName: c.customerName || c.customer_name || 'Client',
                creditCount:  c.creditCount  || c.credit_count || 0,
                balance:      c.balance || 0,
                oldestCredit: c.oldestCredit || c.oldest_credit || null,
              })),
            },

            // ── 5. Paiements ──
            payments: ensureArray(d.payments).map(p => ({
              method:  p.method || 'CASH',
              label:   p.label || p.methodLabel || p.method || 'Inconnu',
              icon:    p.icon || '💰',
              count:   p.count || 0,
              amount:  p.amount || 0,
              percent: p.percent || p.percentage || 0,
            })),

            // ── 6. Équipe ──
            team: ensureArray(d.team).map((m, i) => ({
              name:            m.name || m.sellerName || 'Employé',
              salesCount:      m.salesCount || m.sales || 0,
              revenue:         m.revenue || 0,
              target:          m.target || 50000,
              progressPercent: m.progressPercent || 0,
              ranking:         m.ranking || (i + 1),
            })),

            // ── 7. Stock ──
            stock: {
              alerts: ensureArray(d.stock?.alerts).map(a => ({
                productId:    a.productId || a.product_id,
                productName:  a.productName || a.product_name || 'Produit',
                categoryName: a.categoryName || a.category_name || 'N/A',
                currentStock: a.quantity ?? a.currentStock ?? a.current_stock ?? 0,
                minThreshold: a.minThreshold || a.min_threshold || 0,
                status: (a.quantity ?? a.currentStock ?? 0) === 0 ? 'CRITICAL' : 'LOW',
              })),
              alertCount:     d.stock?.alertCount ?? 0,
              movements:      d.stock?.movements || {},
              lowStockCount:  d.stock?.lowStockCount ?? 0,
              outOfStockCount: d.stock?.outOfStockCount ?? 0,
              totalQuantity:  d.stock?.totalQuantity ?? 0,
            },

            // ── 8. Catégories ──
            categories: ensureArray(d.categories).map(c => ({
              categoryId:   c.categoryId || c.category_id,
              categoryName: c.categoryName || c.category_name || '',
              icon:         c.icon || '📦',
              productCount: c.productCount || c.product_count || 0,
              totalStock:   c.totalStock || c.total_stock || 0,
              monthlySales: c.monthlySales || c.monthly_sales || 0,
            })),
          }
        };
      }

      // Réponse non valide → fallback
      console.warn('⚠️ /reports/full réponse invalide, fallback legacy');
      return reportService._legacyFallback();

    } catch (error) {
      console.error('❌ getFullReport erreur:', error.message || error);
      return reportService._legacyFallback();
    }
  },

  // ─────────────────────────────────────────────
  // FALLBACK LEGACY — si /reports/full pas déployé
  // Utilise les anciens endpoints /manager/stats/*
  // ─────────────────────────────────────────────
  _legacyFallback: async () => {
    console.log('🔄 [LEGACY] Chargement via anciens endpoints...');

    const empty = {
      period: 'today',
      summary: { revenue: 0, salesCount: 0, averageBasket: 0, revenueGrowth: 0, salesGrowth: 0, expenses: 0, netProfit: 0, profitMargin: 0, creditBalance: 0 },
      revenueTimeline: [],
      products: { topSelling: [], abcSummary: {}, abcDetails: [], neverSold: [], neverSoldCount: 0 },
      customers: { stats: { total: 0, vipCount: 0 }, topByRevenue: [], creditDebtors: [] },
      payments: [],
      team: [],
      stock: { alerts: [], alertCount: 0, movements: {}, lowStockCount: 0, outOfStockCount: 0, totalQuantity: 0 },
      categories: [],
    };

    try {
      const [todayRes, topProdRes, teamRes, creditRes] = await Promise.allSettled([
        apiCall.get('/manager/stats/today'),
        apiCall.get('/manager/stats/top-products?limit=10'),
        apiCall.get('/manager/team/performance'),
        apiCall.get('/manager/stats/credit-balance'),
      ]);

      // Stats du jour
      if (todayRes.status === 'fulfilled' && todayRes.value?.success) {
        const d = todayRes.value.data;
        empty.summary = {
          ...empty.summary,
          revenue:       d.total_revenue ?? d.totalRevenue ?? 0,
          salesCount:    d.sale_count ?? d.saleCount ?? 0,
          averageBasket: d.average_sale ?? d.averageSale ?? 0,
        };
      }

      // Top produits
      if (topProdRes.status === 'fulfilled' && topProdRes.value?.success) {
        empty.products.topSelling = ensureArray(topProdRes.value.data).map(p => ({
          productId:    p.product_id || p.productId || p.id,
          productName:  p.product_name || p.productName || p.name || 'Produit',
          categoryName: p.category_name || p.categoryName || 'N/A',
          totalSold:    p.total_sold || p.totalSold || 0,
          totalRevenue: p.total_revenue || p.totalRevenue || 0,
          saleCount:    p.sale_count || p.saleCount || 0,
          currentStock: p.current_stock || p.currentStock || 0,
          todaySold:    p.today_sold || p.todaySold || 0,
        }));
      }

      // Équipe
      if (teamRes.status === 'fulfilled' && teamRes.value?.success) {
        const sellers = teamRes.value.data?.sellers || ensureArray(teamRes.value.data);
        empty.team = sellers.map((s, i) => ({
          name:            s.name || s.sellerName || 'Employé',
          salesCount:      s.salesToday || s.salesCount || s.sales || 0,
          revenue:         s.revenueToday || s.revenue || 0,
          target:          50000,
          progressPercent: 0,
          ranking:         i + 1,
        }));
      }

      // Crédit
      if (creditRes.status === 'fulfilled' && creditRes.value?.success) {
        empty.summary.creditBalance = creditRes.value.data || 0;
      }

    } catch (err) {
      console.error('❌ [LEGACY] fallback erreur:', err);
    }

    return { success: true, data: empty };
  },

  // ─────────────────────────────────────────────
  // MÉTHODES INDIVIDUELLES (compatibilité)
  // Si un autre composant appelle directement
  // reportService.getSalesStats(), ça fonctionne
  // ─────────────────────────────────────────────

  getSalesStats: async (filters = {}) => {
    const r = await reportService.getFullReport(filters.period || 'today');
    return { success: true, data: r.data?.summary || {} };
  },

  getRevenueData: async (filters = {}) => {
    const r = await reportService.getFullReport(filters.period || 'today');
    return { success: true, data: r.data?.revenueTimeline || [] };
  },

  getTopProducts: async (filters = {}) => {
    const r = await reportService.getFullReport(filters.period || 'today');
    return { success: true, data: r.data?.products?.topSelling || [] };
  },

  getTopCustomers: async (filters = {}) => {
    const r = await reportService.getFullReport(filters.period || 'today');
    return { success: true, data: r.data?.customers?.topByRevenue || [] };
  },

  getPaymentMethodsStats: async (filters = {}) => {
    const r = await reportService.getFullReport(filters.period || 'today');
    return { success: true, data: r.data?.payments || [] };
  },

  getTeamPerformance: async (filters = {}) => {
    const r = await reportService.getFullReport(filters.period || 'today');
    return { success: true, data: r.data?.team || [] };
  },

  getCreditStats: async (filters = {}) => {
    const r = await reportService.getFullReport(filters.period || 'today');
    const s = r.data?.summary || {};
    return {
      success: true,
      data: {
        totalCreditAmount: 0,
        totalPaidAmount: 0,
        unpaidAmount: s.creditBalance || 0,
        creditSalesCount: r.data?.customers?.creditDebtors?.length || 0,
        paymentRate: 0,
      }
    };
  },

  getStockAlerts: async (filters = {}) => {
    const r = await reportService.getFullReport(filters.period || 'today');
    return { success: true, data: r.data?.stock?.alerts || [] };
  },

  getCategoryStats: async (filters = {}) => {
    const r = await reportService.getFullReport(filters.period || 'today');
    return { success: true, data: r.data?.categories || [] };
  },

  // Alias pour compatibilité avec ancien code
  getCompleteDashboardReport: async (filters = {}) => {
    return reportService.getFullReport(
      filters.period || 'today',
      filters.startDate || null,
      filters.endDate || null
    );
  },
};

export default reportService;